import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Paragraph, Skeleton, Tabs } from '@digdir/designsystemet-react';
import { useSurveyActions } from '../../hooks/useSurveyActions';
import type {
  WorkEnvironmentEvaluation,
  WorkEnvironmentSurvey,
} from '../../api/workEnvironmentTypes';
import { useArchiveQuery } from '../../hooks/queries/useArchiveQuery';
import { useEvaluationQuery } from '../../hooks/queries/useEvaluationQuery';
import {
  useRemindWorkEnvironmentTeamMutation,
  useWorkEnvironmentSurveyProgressQuery,
} from '../../hooks/queries/useWorkEnvironmentSurveyProgress';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { AnsweredCard } from './components/AnsweredCard';
import { reminderStatus } from './reminderView';
import { ProcessHero } from './components/ProcessHero';
import { ReportCard } from './components/ReportCard';
import { ShareCard } from './components/ShareCard';
import { ArchiveCard } from './evaluation/ArchiveCard';
import { ChangesCard } from './evaluation/ChangesCard';
import { CompareCard } from './evaluation/CompareCard';
import { TeamInspectionReport } from '../WorkEnvironmentInspectionReport/TeamInspectionReport';
import { FocusResultCard } from './evaluation/FocusResultCard';
import { InsufficientEvaluationCard } from './evaluation/InsufficientEvaluationCard';
import { PreviousCard } from './evaluation/PreviousCard';
import { ResidualRiskCard } from './evaluation/ResidualRiskCard';
import { SummaryCard } from './evaluation/SummaryCard';
import {
  EvaluationPhase,
  evaluationPhase,
  comparedPeriods,
  evaluationStep,
} from './evaluationView';
import { currentStepIndex, dayAndMonth, surveyLink } from './surveyProcessView';
import process from './WorkEnvironmentSurveyProcess.module.css';
import classes from './WorkEnvironmentSurveyRisk.module.css';
import evaluationClasses from './evaluation/Evaluation.module.css';
import { archivedAfterEvaluation } from './archiveView';

const TAG_COLORS: Record<EvaluationPhase, 'info' | 'warning' | 'success'> = {
  answering: 'info',
  calculating: 'info',
  insufficient: 'warning',
  result: 'success',
};

/** While the team answers: who has answered, and when the deadline is */
const Answering = ({
  data,
}: {
  data: WorkEnvironmentEvaluation;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const { data: progress } = useWorkEnvironmentSurveyProgressQuery(
    data.evaluation.id,
  );
  const remind = useRemindWorkEnvironmentTeamMutation(data.evaluation.id);
  return (
    <>
      {progress?.responseDeadline && (
        <Alert data-color="success" data-size="sm">
          <Paragraph data-size="sm">
            {t('workEnvironmentEvaluation.sent', {
              date: dayAndMonth(progress.responseDeadline, i18n.language),
            })}
          </Paragraph>
        </Alert>
      )}
      {progress && (
        <AnsweredCard
          progress={progress}
          remind={{
            run: () => remind.mutate(),
            pending: remind.isPending,
            ...reminderStatus(remind, t, i18n.language),
          }}
        />
      )}
    </>
  );
};

type View = 'result' | 'documentation';

/** The result with the residual risk and the checklist, and the documentation for an inspection */
const Result = ({
  survey,
  data,
  result,
}: {
  survey: WorkEnvironmentSurvey;
  data: WorkEnvironmentEvaluation;
  result: NonNullable<WorkEnvironmentEvaluation['result']>;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  const [view, setView] = useState<View>('result');
  const { data: archive, isError } = useArchiveQuery(
    survey.id,
    questionLanguageFor(i18n.language),
  );
  const showDocumentation = () => {
    setView('documentation');
    window.scrollTo({ top: 0 });
  };

  return (
    <Tabs
      value={view}
      onChange={(value) =>
        setView(value === 'documentation' ? 'documentation' : 'result')
      }
      className={evaluationClasses.fullWidth}
    >
      <Tabs.List aria-label={t('workEnvironmentArchive.tabs.label')}>
        <Tabs.Tab value="result">
          {t('workEnvironmentArchive.tabs.result')}
        </Tabs.Tab>
        <Tabs.Tab value="documentation">
          {t('workEnvironmentArchive.tabs.documentation')}
        </Tabs.Tab>
      </Tabs.List>
      <Tabs.Panel value="result">
        <div className={evaluationClasses.panelLayout}>
          <div className={classes.mainColumn}>
            <FocusResultCard data={data} />
            <ChangesCard data={data} areas={result.areas} />
            {isError && (
              <Alert data-color="danger">
                <Paragraph>{t('workEnvironmentArchive.error')}</Paragraph>
              </Alert>
            )}
            {archive && (
              <ResidualRiskCard
                surveyId={survey.id}
                risks={archive.residual}
                choices={archive}
                canDecide={
                  (archive.canDecideResidual ?? archive.canArchive) &&
                  may('decideResidual')
                }
              />
            )}
          </div>
          <div className={process.side}>
            <SummaryCard areas={result.areas} />
            {archive && (
              <ArchiveCard
                surveyId={survey.id}
                archive={archive}
                onShowDocumentation={showDocumentation}
              />
            )}
          </div>
        </div>
      </Tabs.Panel>
      <Tabs.Panel value="documentation">
        <TeamInspectionReport surveyId={survey.id} embedded />
      </Tabs.Panel>
    </Tabs>
  );
};

/** Step 9: the team answers the same survey again, and the result is compared with the first */
export const WorkEnvironmentSurveyEvaluation = ({
  survey,
}: {
  survey: WorkEnvironmentSurvey;
}): ReactElement => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  const { data, isLoading, isError } = useEvaluationQuery(
    survey.id,
    questionLanguageFor(i18n.language),
  );
  const phase = data ? evaluationPhase(data) : null;
  const result = phase === 'result' && data?.result ? data.result : null;
  const step = evaluationStep(
    phase ?? 'answering',
    archivedAfterEvaluation(survey),
  );

  return (
    <div data-size="md">
      <ProcessHero
        title={t(
          result
            ? 'workEnvironmentEvaluation.result.heading'
            : 'workEnvironmentEvaluation.heading',
        )}
        surveyTitle={survey.title}
        intro={
          data && result
            ? t('workEnvironmentEvaluation.result.intro', {
                ...comparedPeriods(
                  data.baseline.createdAt,
                  data.evaluation.createdAt,
                  i18n.language,
                ),
                responsesBefore: data.baseline.responses,
                responsesAfter: result.responses,
              })
            : t('workEnvironmentEvaluation.intro')
        }
        tag={
          archivedAfterEvaluation(survey)
            ? { label: t('workEnvironmentArchive.tag'), color: 'success' }
            : {
                label: t(
                  `workEnvironmentEvaluation.tag.${phase === 'result' ? 'done' : (phase ?? 'answering')}`,
                ),
                color: TAG_COLORS[phase ?? 'answering'],
              }
        }
        process={{
          current: currentStepIndex(survey.status),
          createdAt: survey.createdAt,
          responseDeadline: null,
          finished: step.finished,
          currentStep: step.awaitingArchive
            ? {
                label: t('workEnvironmentArchive.step.label'),
                text: t('workEnvironmentArchive.step.text'),
              }
            : null,
        }}
      />
      <div className={process.layout}>
        {isLoading && (
          <Skeleton variant="rectangle" width="100%" height={320} />
        )}
        {isError && (
          <Alert data-color="danger">
            <Paragraph>{t('workEnvironmentEvaluation.error')}</Paragraph>
          </Alert>
        )}
        {data && result && (
          <Result survey={survey} data={data} result={result} />
        )}
        {data && !result && (
          <>
            <div className={classes.mainColumn}>
              {phase === 'answering' && <Answering data={data} />}
              {phase === 'calculating' && (
                <Alert data-color="info" data-size="sm">
                  <Paragraph data-size="sm">
                    {t('workEnvironmentEvaluation.calculating')}
                  </Paragraph>
                </Alert>
              )}
              {phase === 'insufficient' && (
                <InsufficientEvaluationCard
                  surveyId={survey.id}
                  evaluationId={data.evaluation.id}
                  responses={data.result?.responses ?? 0}
                  canExtend={
                    (data.canExtend ?? data.canFollowUp) && may('advance')
                  }
                />
              )}
              <CompareCard data={data} />
            </div>
            <div className={process.side}>
              {phase !== 'calculating' && (
                <ShareCard
                  link={surveyLink(
                    window.location.origin,
                    data.evaluation.uuid,
                  )}
                  sharing={{ surveyId: survey.id, teamId: survey.teamId }}
                />
              )}
              <ReportCard surveyId={survey.id} />
              <PreviousCard baseline={data.baseline} />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
